import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Landmark, Loader2, Unlink, ChevronDown, ChevronRight, FileText, CreditCard, Search } from "lucide-react";

const eur = (n) => new Intl.NumberFormat("nl-BE", { style: "currency", currency: "EUR" }).format(Number(n || 0));
const SRC = { viva: "Viva", bnp: "BNP" };

export default function FactuurboekLeveranciersrekening() {
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState({});
  const [unlinkBusy, setUnlinkBusy] = useState(false);
  const [query, setQuery] = useState("");

  const load = () => {
    setLoading(true);
    api.get("/factuurboek/leveranciersrekening")
      .then((r) => setData(r.data))
      .catch(() => setData({ suppliers: [], open_total: 0, unaccounted_total: 0 }))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const unlinkAll = async () => {
    if (!window.confirm(
      "ALLE Viva- én BNP-koppelingen met facturen losmaken?\n\n" +
      "• De betalingen worden van de facturen verwijderd en de betaalstatus wordt herberekend.\n" +
      "• De kunstmatige betaalboekingen worden verwijderd.\n" +
      "• De échte bankimports (Viva/BNP-transacties) blijven volledig behouden — enkel de koppeling wordt gereset.\n\n" +
      "Doorgaan?")) return;
    setUnlinkBusy(true);
    try {
      const r = await api.post("/factuurboek/unlink-all-bank");
      toast.success(`${r.data.payments_removed} betaling(en) losgekoppeld op ${r.data.invoices_reset} factuur/facturen · ${r.data.entries_unlinked} banktransacties gereset · ${r.data.bookings_deleted} boekingen verwijderd`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Ontkoppelen mislukt"); }
    finally { setUnlinkBusy(false); }
  };

  const toggle = (name) => setOpen((o) => ({ ...o, [name]: !o[name] }));
  const q = query.trim().toLowerCase();
  const suppliers = (data?.suppliers || []).filter((s) => {
    if (!q) return true;
    if (s.supplier_name.toLowerCase().includes(q)) return true;
    if (s.open_invoices.some((inv) => (inv.invoice_number || "").toLowerCase().includes(q))) return true;
    if (s.unaccounted_payments.some((p) => (p.ref || "").toLowerCase().includes(q) || (p.account || "").toLowerCase().includes(q))) return true;
    return false;
  });

  return (
    <div className="space-y-6" data-testid="leveranciersrekening-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="font-heading text-3xl font-semibold tracking-tight flex items-center gap-2">
            <Landmark className="h-7 w-7 text-indigo-600" /> Leveranciersrekening
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Per leverancier: openstaande facturen en bankbetalingen die nog verantwoord/gekoppeld moeten worden.</p>
        </div>
        {isAdmin && (
          <Button variant="outline" className="text-red-600 border-red-300 hover:bg-red-50" disabled={unlinkBusy} data-testid="unlink-all-bank-btn" onClick={unlinkAll}>
            {unlinkBusy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Unlink className="h-4 w-4 mr-2" />} Alle Viva/BNP-koppelingen losmaken
          </Button>
        )}
      </div>

      {!loading && data && (
        <div className="grid grid-cols-2 gap-3 max-w-md">
          <Card className="shadow-sm"><CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Totaal openstaand</div>
            <div className="text-2xl font-semibold tabular-nums" data-testid="open-total">{eur(data.open_total)}</div>
          </CardContent></Card>
          <Card className="shadow-sm"><CardContent className="p-4">
            <div className="text-xs text-muted-foreground">Nog te verantwoorden (betalingen)</div>
            <div className="text-2xl font-semibold tabular-nums text-amber-600" data-testid="unaccounted-total">{eur(data.unaccounted_total)}</div>
          </CardContent></Card>
        </div>
      )}

      {!loading && (data?.suppliers || []).length > 0 && (
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Zoek op leverancier, factuurnummer of rekening…"
            className="pl-9"
            data-testid="leveranciersrekening-search"
          />
        </div>
      )}

      {loading ? (
        <div className="py-16 text-center text-muted-foreground">Laden…</div>
      ) : suppliers.length === 0 ? (
        <Card className="shadow-sm"><CardContent className="py-16 text-center text-muted-foreground">{q ? `Geen resultaten voor "${query}".` : "Geen openstaande facturen of niet-gekoppelde betalingen."}</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {suppliers.map((s) => {
            const isOpen = open[s.supplier_name] ?? true;
            return (
              <Card key={s.supplier_name} className="shadow-sm" data-testid={`supplier-ledger-${s.supplier_name}`}>
                <CardContent className="p-0">
                  <button className="w-full flex items-center justify-between gap-3 px-4 py-3 hover:bg-accent/40 transition-colors" onClick={() => toggle(s.supplier_name)} data-testid={`supplier-toggle-${s.supplier_name}`}>
                    <div className="flex items-center gap-2 min-w-0">
                      {isOpen ? <ChevronDown className="h-4 w-4 shrink-0" /> : <ChevronRight className="h-4 w-4 shrink-0" />}
                      <span className="font-medium truncate">{s.supplier_name}</span>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      {s.open_total > 0 && <Badge variant="outline" className="tabular-nums">Open {eur(s.open_total)}</Badge>}
                      {s.unaccounted_total > 0 && <Badge className="bg-amber-500 tabular-nums">Te verantwoorden {eur(s.unaccounted_total)}</Badge>}
                    </div>
                  </button>
                  {isOpen && (
                    <div className="border-t divide-y">
                      {s.open_invoices.length > 0 && (
                        <div className="p-3 space-y-1.5">
                          <div className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> Openstaande facturen</div>
                          {s.open_invoices.map((inv) => (
                            <div key={inv.id} className="flex items-center justify-between gap-2 text-sm py-1" data-testid={`open-invoice-${inv.id}`}>
                              <span className="min-w-0 truncate">
                                {inv.is_credit && <Badge className="bg-red-500 mr-1.5 text-[10px]">CN</Badge>}
                                {inv.invoice_number || "—"} · {inv.invoice_date}
                                {inv.paid > 0 && <span className="text-muted-foreground"> · reeds {eur(inv.paid)}</span>}
                                {inv.pending_paid > 0 && <span className="text-amber-600"> · {eur(inv.pending_paid)} gekoppeld (onbevestigd)</span>}
                              </span>
                              <span className={`tabular-nums shrink-0 font-medium ${inv.open < 0 ? "text-red-600" : ""}`}>{eur(inv.open)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                      {s.unaccounted_payments.length > 0 && (
                        <div className="p-3 space-y-1.5 bg-amber-50/40">
                          <div className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5"><CreditCard className="h-3.5 w-3.5" /> Nog te verantwoorden betalingen</div>
                          {s.unaccounted_payments.map((p) => (
                            <div key={p.id} className="flex items-center justify-between gap-2 text-sm py-1" data-testid={`unaccounted-payment-${p.id}`}>
                              <span className="min-w-0 truncate">
                                <Badge variant="outline" className="mr-1.5 text-[10px]">{SRC[p.source] || p.source}</Badge>
                                {p.date} · <span className="font-mono text-xs">{p.account}</span>{p.ref ? ` · ${p.ref}` : ""}
                              </span>
                              <span className="tabular-nums shrink-0 font-medium text-amber-700">{eur(p.amount)}</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
